import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DEEP_PIVOT_COLUMNS, deepPivotCsv, deepPivotFilename } from '../utils/deepPivotCsv';
import { API_BASE_URL } from '../constants';

/**
 * DEEP PIVOT RESEARCH — the admin view.
 *
 * <h2>The table renders from the EXPORT's column list</h2>
 *
 * {@code DEEP_PIVOT_COLUMNS} is the single declaration of what a row contains, and both the table
 * and the CSV read it. The export is the product here — the whole feature exists to get this into
 * a spreadsheet — so a column present on screen and missing from the file would be the worst of
 * the two failures, and one list makes it impossible.
 *
 * <h2>The commentary sits ABOVE the table, including its caveats</h2>
 *
 * The backend returns findings and the reasons not to over-read them in the same block, and both
 * are rendered. A summary that printed "75% of A1 tags were Extension Highs" without the base
 * rate beside it would be the confluence mistake again: a share that looks selective and is only
 * reporting how common the slice already was.
 *
 * <h2>THE TABLE IS DEPRECATED. The scan paints the chart instead.</h2>
 *
 * A thousand rows of dates in a dialog is the wrong instrument for a question about where turns
 * fall in the price action — the reader was being asked to hold a date in their head and go
 * looking for it. {@link deepPivotOverlay} puts an arrow on each tagged pivot, so the answer is
 * read against the candles rather than carried to them.
 *
 * <p>A GATE, not a deletion. The table renders from {@code DEEP_PIVOT_COLUMNS} and so does the
 * CSV, which is the actual product of this feature; commenting the JSX out would stop it being
 * compiled or linted and the two would silently drift until the export broke. Flip
 * {@code SHOW_DEEP_PIVOT_TABLE} to see it again.
 */

/**
 * The deprecated table. See the note above — kept live, rendered off.
 *
 * <p>The commentary block, the controls and the CSV export are NOT gated by this: the commentary
 * is the study's own conclusion (including the control metric that says the tags are not
 * predictive), and the export is what anyone actually analysing this uses.
 */
export const SHOW_DEEP_PIVOT_TABLE = false;

const DeepPivotDialog = ({ open, onClose, isAdmin, symbol, token, onPaint }) => {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [orbPolicy, setOrbPolicy] = useState('AVERAGE');
  /**
   * THE MOON IS OFF BY DEFAULT, and that is a measured decision rather than a preference.
   *
   * <p>Its ~13 degrees a day made every dynamic orb a dragnet: 89.6% of SPY pivots carried a tag
   * against 89.8% of ordinary sessions, a lift of exactly 1.00x. It is a toggle rather than a
   * deletion because the Moon is the fastest thing in the sky and somebody will want to ask
   * about it deliberately -- but it should be asked for, not inherited.
   */
  const [includeMoon, setIncludeMoon] = useState(false);

  /**
   * The scan this report belongs to.
   *
   * <p>A STALE-RESPONSE GUARD. Toggling the Moon and then the orb policy starts two scans, and
   * the first is the slower of the two (the Moon multiplies the aspect search). Without this the
   * older answer lands last and is displayed under the newer settings — a table that is not
   * merely out of date but mislabelled, which is the one failure this dialog cannot afford given
   * it exists to attribute market turns to a configuration of the sky.
   */
  const scanId = useRef(0);

  /**
   * THE PAINT CALLBACK, HELD IN A REF, and that is load-bearing rather than tidy.
   *
   * <p>`run` is a `useCallback` and the effect below re-runs whenever its identity changes. A
   * parent passing an inline `onPaint={() => ...}` — which is how every other dialog in this file
   * is wired — makes a new function on every render, so putting it in `run`'s dependency array
   * would change `run` on every render, fire the effect, scan, set state, render, and scan again.
   * An unbounded loop of an ephemeris scan, triggered by the ordinary way of passing a prop.
   */
  const paintRef = useRef(onPaint);
  paintRef.current = onPaint;

  const run = useCallback(async () => {
    const mine = (scanId.current += 1);
    setRunning(true);
    setError('');
    setReport(null);
    try {
      const url = `${API_BASE_URL}/api/deep-pivot/scan`
        + `?symbol=${encodeURIComponent(symbol)}&orbPolicy=${encodeURIComponent(orbPolicy)}`
        // SENT EXPLICITLY, both ways. A falsy check that dropped `false` would let the server's
        // own default decide -- which is the same value today and is exactly how a lever comes
        // to be shown in the UI and ignored on the wire.
        + `&includeMoon=${includeMoon ? 'true' : 'false'}`;
      const response = await fetch(url, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        // The SERVER'S OWN message where there is one. A 403 here means the admin gate refused,
        // and "Request failed with status 403" would send the reader looking at the network.
        let detail = `HTTP ${response.status}`;
        try {
          const body = await response.json();
          if (body && body.error) detail = body.error;
        } catch (ignored) { /* a non-JSON error body is not worth a second failure */ }
        throw new Error(detail);
      }
      const body = await response.json();
      if (mine !== scanId.current) return;   // superseded -- a newer scan owns the panel
      setReport(body);
      // PAINTS THE CHART. Behind the same supersession guard as the panel, so a slow scan cannot
      // leave the canvas showing one configuration's arrows under another's commentary.
      if (typeof paintRef.current === 'function') paintRef.current(body);
    } catch (e) {
      if (mine !== scanId.current) return;
      setError(e.message || String(e));
    } finally {
      if (mine === scanId.current) setRunning(false);
    }
  }, [symbol, token, orbPolicy, includeMoon]);

  /**
   * THE CONTROLS RE-RUN THE SCAN. Without this they do nothing visible and lie about it.
   *
   * <p>Found in a browser, not by a test. `run` was a `useCallback` over exactly these
   * dependencies and NOTHING CALLED IT when they changed, so toggling the Moon left the previous
   * report on screen — same 1284 pivots, same 73.1%, to the digit. The reader's conclusion would
   * have been "the Moon makes no difference", which is a finding, drawn from a scan that never
   * ran. The orb-policy select was inert in exactly the same way.
   *
   * <p>It passed its own test. That test asserted the dependency array
   * `[symbol, token, orbPolicy, includeMoon]` appeared in the source — and it did, on the
   * `useCallback`. A dependency list is what a value is rebuilt FROM; it is not something that
   * invokes anything. The test below now pins the EFFECT.
   *
   * <p>Keyed on `run` rather than on the four values: `run`'s identity already changes with
   * them, so a lever added to the callback is covered here by construction instead of needing to
   * be remembered in a second list.
   */
  useEffect(() => {
    if (!open || !isAdmin || !token) return;
    run();
  }, [open, isAdmin, token, run]);

  const exportCsv = useCallback(() => {
    if (!report || !report.rows || report.rows.length === 0) return;
    const blob = new Blob([deepPivotCsv(report.rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = deepPivotFilename(report.symbol, new Date().toISOString().slice(0, 10));
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoked on a delay: Chrome cancels a download whose blob URL is released before it has
    // finished reading it. The same reason operatorExport does it.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }, [report]);

  if (!isAdmin) return null;

  const rows = (report && report.rows) || [];

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="xl" fullWidth
      data-testid="deep-pivot-dialog">
      <DialogTitle sx={{ pb: 1, fontSize: 16, fontWeight: 700 }}>
        Deep Pivot Research — {symbol}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
          Every 3-bar swing pivot, its leg geometry, and the sky over it. Admin only.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" size="small" onClick={run} disabled={running}
            data-testid="deep-pivot-run">
            {running ? 'Scanning…' : 'Run Deep Pivot Celestial Scan'}
          </Button>
          {running && <CircularProgress size={18} />}
          <TextField select size="small" label="Orb policy" value={orbPolicy}
            onChange={(e) => setOrbPolicy(e.target.value)} sx={{ minWidth: 220 }}
            data-testid="deep-pivot-orb-policy"
            helperText="How two daily traversals become one allowed orb">
            <MenuItem value="AVERAGE">Average of the two traversals</MenuItem>
            <MenuItem value="FASTER">The faster body&apos;s traversal</MenuItem>
            <MenuItem value="RELATIVE">Relative speed (not specified; see notes)</MenuItem>
          </TextField>
          <FormControlLabel
            control={(
              <Checkbox size="small" checked={includeMoon} data-testid="deep-pivot-include-moon"
                onChange={(e) => setIncludeMoon(e.target.checked)} />
            )}
            label={<Typography variant="body2">Include the Moon</Typography>}
          />
          <Button variant="outlined" size="small" onClick={exportCsv}
            disabled={rows.length === 0} data-testid="deep-pivot-export">
            Export to CSV{rows.length > 0 ? ` (${rows.length})` : ''}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="deep-pivot-error">{error}</Alert>}

        {report && (
          <Box sx={{ mb: 2 }} data-testid="deep-pivot-commentary">
            <Alert severity="info" icon={false} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {report.commentary.headline}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {report.barCount} bars, {report.from} to {report.to} · {report.rawPivots} raw
                pivots, {report.collapsed} absorbed into same-type runs · orb policy{' '}
                {report.parameters.orbPolicy}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              {Object.entries(report.commentary.categoryCounts).map(([tag, n]) => (
                <Chip key={tag} size="small" label={`${tag}: ${n}`}
                  color={n > 0 && tag !== '—' ? 'primary' : 'default'}
                  variant={n > 0 && tag !== '—' ? 'filled' : 'outlined'} />
              ))}
            </Box>

            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {report.commentary.findings.map((f) => (
                <Typography component="li" key={f} variant="body2" sx={{ mb: 0.25 }}>{f}</Typography>
              ))}
            </Box>

            {/* THE CAVEATS ARE RENDERED, not collapsed behind a toggle. They are the half of the
                commentary that stops a share being read as a result. */}
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
              {report.commentary.caveats.map((c) => (
                <Typography component="li" key={c} variant="caption"
                  sx={{ display: 'list-item', color: 'text.secondary' }}>{c}</Typography>
              ))}
            </Box>

            {report.orbPolicyCounts && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Aspects in orb under each policy —{' '}
                {Object.entries(report.orbPolicyCounts)
                  .map(([p, n]) => `${p}: ${n.toLocaleString()}`).join(' · ')}
                . The specification offers the first two readings; they give materially different
                studies, so both are measured on every run.
              </Typography>
            )}
          </Box>
        )}

        {SHOW_DEEP_PIVOT_TABLE && rows.length > 0 && (
          <Box sx={{ maxHeight: '48vh', overflow: 'auto', border: '1px solid',
            borderColor: 'divider' }}>
            <Box component="table" data-testid="deep-pivot-table"
              sx={{ borderCollapse: 'collapse', width: '100%', fontSize: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
              <Box component="thead" sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <Box component="tr">
                  {DEEP_PIVOT_COLUMNS.map(([header]) => (
                    <Box component="th" key={header}
                      sx={{ textAlign: 'left', p: 0.75, bgcolor: 'background.paper',
                        borderBottom: '2px solid', borderColor: 'divider', whiteSpace: 'nowrap' }}>
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map((r, i) => (
                  <Box component="tr" key={`${r.date}-${r.pivotType}-${i}`}
                    sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                    {DEEP_PIVOT_COLUMNS.map(([header, read]) => (
                      <Box component="td" key={header}
                        sx={{ p: 0.75, borderBottom: '1px solid', borderColor: 'divider',
                          whiteSpace: 'nowrap' }}>
                        {read(r)}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {report && rows.length === 0 && !error && (
          <Alert severity="warning">
            No pivots were produced. That is a statement about the data rather than about the
            sky — check the symbol has stored history.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

DeepPivotDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Renders nothing when false. The caller gates too. */
  isAdmin: PropTypes.bool,
  symbol: PropTypes.string,
  token: PropTypes.string,
  /** Called with the scan report so the caller can paint it onto the chart. */
  onPaint: PropTypes.func,
};

DeepPivotDialog.defaultProps = {
  open: false,
  isAdmin: false,
  symbol: '',
  token: '',
  onPaint: undefined,
};

export default DeepPivotDialog;
